import React, { useState, useEffect } from "react";
import CustomDataGrid from "../components/CustomDataGrid";
import {
    generateGridRows,
    generateGridColumns,
    usageMetricGridOptionalColumns,
    usageMetricsRowMapping,
    usageMetricGridColumns,
    formatUtcToLocal
} from "../utils/PageUtils";
import EntityDetails from "../components/EntityDetails";
import QuotaBarChart from "../components/Barchart";
import { Alert, Tab, Tabs } from "react-bootstrap";
import "../styles/UsageTracker.css";
import PageHelmet from "../components/PageHelmet";
import { FiFileText, FiBarChart2 } from "react-icons/fi"; // icons for tabs
import { MdArrowBackIos } from "react-icons/md";
import { fetchUsageMetric } from "../utils/api";

const allowedQuotaKeys = ["campaigns", "contacts", "templates", "requests"];

const filterQuotaKeys = (obj = {}) =>
    Object.fromEntries(
        Object.entries(obj).filter(([key]) => allowedQuotaKeys.includes(key.toLowerCase()))
    );

const formatQuotaObject = (obj = {}) => {
    const filtered = filterQuotaKeys(obj);
    const entries = Object.entries(filtered);
    if (entries.length === 0) return "-";
    return entries
        .map(([key, value]) => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`)
        .join("\n");
};

const UsageTracker = ({ userRole, bearerToken }) => {
    const [usageMetric, setUsageMetric] = useState([]);
    const [usageMetricError, setUsageMetricError] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectedUsageMetric, setSelectedUsageMetric] = useState(null);
    const [quotaData, setQuotaData] = useState([]);
    const [activeTab, setActiveTab] = useState("details"); // Default tab

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    useEffect(() => {
        if (selectedUsageMetric && isMobile) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [selectedUsageMetric, isMobile]);

    useEffect(() => {
        const fetchQuotaUsages = async () => {
            try {
                const response = await fetchUsageMetric(bearerToken)
                if (response.success) {
                    const usageMetrics = response.data.records;
                    const sortedRecords = usageMetrics.toSorted((a, b) => new Date(b.start_date) - new Date(a.start_date));

                    setUsageMetric(sortedRecords);
                    if (!isMobile) {
                        setSelectedUsageMetric(response.data.records[0]); // Select first row by default on desktop
                        setSelectedRowId(1);
                    }
                } else {
                    setUsageMetricError(response.message || "Failed to fetch usage metrics");
                }
            } catch (error) {
                setUsageMetricError("Failed to fetch usage metrics");
            }
        };
        fetchQuotaUsages();
    }, [isMobile]);

    useEffect(() => {
        const selected = selectedUsageMetric || usageMetric[0]; // fallback to first row
        if (selected) {
            const limitObj = filterQuotaKeys(selected.quota_limit || selected.quota || {});
            const usedObj = filterQuotaKeys(selected.quota_used || selected.used_quota || {});
            const data = Object.keys(limitObj).map((key) => {
                const used = Number(usedObj?.[key] || 0);
                const total = Number(limitObj?.[key] || 0);
                const unused = total - used;
                return {
                    name: key.charAt(0).toUpperCase() + key.slice(1),
                    used,
                    unused: unused < 0 ? 0 : unused,
                };
            });

            // Add Credits to the chart if they exist
            if (selected.credit_limit !== undefined || selected.credit_used !== undefined) {
                const creditLimit = Number(selected.credit_limit || 0);
                const creditUsed = Number(selected.credit_used || 0);
                const unusedCredits = creditLimit - creditUsed;
                data.unshift({
                    name: "Credits",
                    used: creditUsed,
                    unused: unusedCredits < 0 ? 0 : unusedCredits,
                });
            }
            setQuotaData(data);
        } else {
            setQuotaData([]);
        }
    }, [usageMetric, selectedUsageMetric]);

    const handleRowClick = (e) => {
        const rowId = e?.row?.id ?? e?.id;
        if (selectedRowId === rowId) {
            setSelectedRowId(null);
            setSelectedUsageMetric(null);
        } else {
            const selected = usageMetric[e.id - 1];
            setSelectedUsageMetric(selected);
            setSelectedRowId(rowId);
        }
        console.log("Selected Row ID:", rowId);
        setActiveTab("details"); // Reset to "Details" tab on new selection
    };

    const handleCloseDetails = () => {
        setSelectedUsageMetric(null);
        setSelectedRowId(null);
    };

    // Inline helper for Tabs
    function UsageDetailsTabs({ activeTab, setActiveTab, details, data }) {
        return (
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="usage-tabs mb-3">
                <Tab
                    eventKey="details"
                    title={
                        <span className="tab-title">
                            <FiFileText style={{ marginRight: 6 }} />
                            Details
                        </span>
                    }
                >
                    <div className="usage-metric-details">
                        <EntityDetails inputs={details} width={"110px"} />
                    </div>
                </Tab>

                <Tab
                    eventKey="quota"
                    title={
                        <span className="tab-title">
                            <FiBarChart2 style={{ marginRight: 6 }} />
                            Quota Chart
                        </span>
                    }
                >
                    <div className="usage-tracker-chart">
                        <QuotaBarChart data={data} />
                    </div>
                </Tab>
            </Tabs>
        );
    }

    const usageMetricForGrid = usageMetric.map((p) => ({
        ...p,
        quota_id: p.quota_id || p.usage_metric_id,
        start_date: p.start_date || "-",
        end_date: p.end_date || "-",
        credit_limit: p.credit_limit || "-",
        credit_used: p.credit_used ?? "-",
        quota_limit: formatQuotaObject(p.quota_limit || p.quota),
        quota_used: formatQuotaObject(p.quota_used || p.used_quota),
    }));

    const usageMetricRows = generateGridRows(
        usageMetricForGrid,
        usageMetricsRowMapping
    );
    const usageMetricColumns = generateGridColumns(
        usageMetricGridColumns,
        "quota_id",
        undefined,
        usageMetricGridOptionalColumns
    );

    const usageMetricFormData = selectedUsageMetric
        ? [
            { label: "Plan Name", value: selectedUsageMetric.plan_name || "-" },
            { label: "Billing Cycle", value: selectedUsageMetric.billing_cycle || "-" },
            { label: "Start Date", value: formatUtcToLocal(selectedUsageMetric.start_date) || "-" },
            { label: "End Date", value: formatUtcToLocal(selectedUsageMetric.end_date) || "-" },
            { label: "Credit Limit", value: selectedUsageMetric.credit_limit ?? "-" },
            { label: "Credit Used", value: selectedUsageMetric.credit_used ?? "-" },
            { label: "Quota Limit", value: formatQuotaObject(selectedUsageMetric.quota_limit || selectedUsageMetric.quota) },
            { label: "Quota Used", value: formatQuotaObject(selectedUsageMetric.quota_used || selectedUsageMetric.used_quota) },
            { label: "Created", value: formatUtcToLocal(selectedUsageMetric.created) || "-" },
            { label: "Updated", value: formatUtcToLocal(selectedUsageMetric.updated) || "-" },
            { label: "Metric ID", value: selectedUsageMetric.usage_metric_id || selectedUsageMetric.quota_id || "-" },
        ]

        : [];

    if (userRole !== "customer") {
        return (
            <div className="user-error">
                <h1>404 - Page Not Found</h1>
                <p>The page you're looking for doesn't exist.</p>
            </div>
        );
    }
    return (
        <div className="usage-tracker-page">
            <PageHelmet
                title="Usage Tracker"
                description="View Quota Usages"
                url={`${process.env.REACT_APP_COGNITO_REDIRECT_URL}/usage_tracker`}
            />

            {usageMetricError && <Alert variant="danger">{usageMetricError}</Alert>}

            <div
                className={`usage-tracker-container ${selectedUsageMetric ? "with-selection" : "no-selection"
                    }`}
            >
                {!usageMetricError && (
                    <div className="usage-metric-grid">
                        <h2 className="usage-tracker-heading">Usage Tracker</h2>
                        <CustomDataGrid
                            rows={usageMetricRows}
                            columns={usageMetricColumns}
                            totalRows={usageMetric.length}
                            onRowClick={handleRowClick}
                            selectedRowId={selectedRowId}
                            selectionModel={selectedRowId ? [selectedRowId] : []}  // <- ensures highlight

                            getRowHeight={() => "auto"}
                        />
                    </div>
                )}

                {selectedUsageMetric && (
                    <div className={`usage-metric-details-wrapper ${isMobile ? 'mobile-open' : ''}`}>
                        <div className="usage-metric-details-header">
                            <div className="mobile-detail-nav">
                                <button className="back-btn" onClick={handleCloseDetails} aria-label="Back">
                                    <MdArrowBackIos size={18} />
                                </button>
                                <div className="mobile-activity-title">{selectedUsageMetric?.usage_metric_id || selectedUsageMetric?.customer_id || 'Details'}</div>
                            </div>
                        </div>

                        <div className="usage-metric-details-body">
                            <UsageDetailsTabs activeTab={activeTab} setActiveTab={setActiveTab} details={usageMetricFormData} data={quotaData} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UsageTracker;